import type {
  CompetitionSubmissionRow,
  LeaderboardRow,
  SubmissionStatus,
  TeamMemberProfileRow,
  TeamScoreBreakdown,
} from "../types/database";
import { supabase } from "./supabase";

function unwrapOne<T>(rel: T | T[] | null | undefined): T | null {
  if (rel == null) return null;
  return Array.isArray(rel) ? (rel[0] ?? null) : rel;
}

export async function getCompetitionLeaderboard(competitionId: string): Promise<LeaderboardRow[]> {
  const { data, error } = await supabase.rpc("get_competition_leaderboard", {
    p_competition_id: competitionId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as LeaderboardRow[];
}

export async function getTeamMembers(teamId: string): Promise<TeamMemberProfileRow[]> {
  const { data, error } = await supabase
    .from("team_members")
    .select("student_id, students(university, user_profiles(full_name))")
    .eq("team_id", teamId);
  if (error || !data) return [];

  return (data as { student_id: string; students: unknown }[]).map((row) => {
    const stu = unwrapOne(
      row.students as
        | { university: string | null; user_profiles: { full_name: string | null } | { full_name: string | null }[] | null }
        | {
            university: string | null;
            user_profiles: { full_name: string | null } | { full_name: string | null }[] | null;
          }[]
        | null
    );
    const profile = stu ? unwrapOne(stu.user_profiles) : null;
    return {
      student_id: row.student_id,
      full_name: profile?.full_name ?? null,
      university: stu?.university ?? null,
    };
  });
}

type RawScoreRow = {
  id: string;
  score: number | null;
  passed_gate: boolean | null;
  rubric_notes: string | null;
  submissions:
    | {
        deliverables:
          | { id: string; name: string; sequence: number; point_value: number | null; is_locked_gate: boolean }
          | { id: string; name: string; sequence: number; point_value: number | null; is_locked_gate: boolean }[]
          | null;
      }
    | {
        deliverables:
          | { id: string; name: string; sequence: number; point_value: number | null; is_locked_gate: boolean }
          | { id: string; name: string; sequence: number; point_value: number | null; is_locked_gate: boolean }[]
          | null;
      }[]
    | null;
};

type RawDeliverableRow = {
  id: string;
  name: string;
  sequence: number;
  point_value: number | null;
  is_locked_gate: boolean;
};

type RawCategoryRow = { id: string; slug: string; name: string; display_order: number };

type RawLineItem = { submission_score_id: string; category_id: string; sub_score: number | null };

export async function getTeamScores(teamId: string): Promise<TeamScoreBreakdown[]> {
  const { data: teamData, error: teamErr } = await supabase
    .from("teams")
    .select("competition_id")
    .eq("id", teamId)
    .maybeSingle();
  if (teamErr || !teamData?.competition_id) return [];
  const competitionId = teamData.competition_id as string;

  const submissionIds =
    (
      await supabase
        .from("submissions")
        .select("id")
        .eq("team_id", teamId)
        .eq("competition_id", competitionId)
    ).data?.map((r) => r.id) ?? [];

  const [{ data: delData }, { data: scoreData }, { data: catData }] = await Promise.all([
    supabase
      .from("deliverables")
      .select("id, name, sequence, point_value, is_locked_gate")
      .eq("competition_id", competitionId)
      .order("sequence", { ascending: true }),
    supabase
      .from("submission_scores")
      .select("id, score, passed_gate, rubric_notes, submissions(deliverables(id, name, sequence, point_value, is_locked_gate))")
      .eq("competition_id", competitionId)
      // Drafts must NEVER surface to clients. RLS enforces this server-side
      // too, but keep the explicit filter so callers can read the intent.
      .eq("status", "submitted")
      .in("submission_id", submissionIds),
    supabase
      .from("rubric_categories")
      .select("id, slug, name, display_order")
      .eq("is_active", true)
      .order("display_order", { ascending: true }),
  ]);

  const allDeliverables = (delData ?? []) as RawDeliverableRow[];
  const cats = (catData ?? []) as RawCategoryRow[];

  // Hydrate per-line-item sub_scores in one shot for every visible header.
  const headerIds = ((scoreData ?? []) as RawScoreRow[]).map((r) => r.id);
  let lineItems: RawLineItem[] = [];
  if (headerIds.length > 0) {
    const { data: liData } = await supabase
      .from("submission_score_categories")
      .select("submission_score_id, category_id, sub_score")
      .in("submission_score_id", headerIds);
    lineItems = (liData ?? []) as RawLineItem[];
  }
  const lineItemsByHeader = new Map<string, Map<string, number | null>>();
  for (const li of lineItems) {
    const inner = lineItemsByHeader.get(li.submission_score_id) ?? new Map<string, number | null>();
    inner.set(li.category_id, li.sub_score);
    lineItemsByHeader.set(li.submission_score_id, inner);
  }

  type DelBucket = {
    name: string;
    sequence: number;
    point_value: number;
    is_locked_gate: boolean;
    judge_scores: TeamScoreBreakdown["judge_scores"];
    category_running: Map<string, { sum: number; count: number }>;
  };

  const map = new Map<string, DelBucket>();
  for (const d of allDeliverables) {
    map.set(d.id, {
      name: d.name,
      sequence: d.sequence,
      point_value: d.point_value ?? 0,
      is_locked_gate: Boolean(d.is_locked_gate),
      judge_scores: [],
      category_running: new Map(),
    });
  }

  for (const row of (scoreData ?? []) as RawScoreRow[]) {
    const sub = unwrapOne(row.submissions);
    const del = sub ? unwrapOne(sub.deliverables) : null;
    if (!del) continue;
    const hit = map.get(del.id);
    if (!hit) continue;

    const li = lineItemsByHeader.get(row.id) ?? new Map<string, number | null>();
    const category_scores = cats.map((c) => ({
      category_id: c.id,
      slug: c.slug,
      name: c.name,
      display_order: c.display_order,
      sub_score: li.has(c.id) ? li.get(c.id) ?? null : null,
    }));

    hit.judge_scores.push({
      score: row.score,
      passed_gate: row.passed_gate ?? null,
      rubric_notes: row.rubric_notes ?? null,
      category_scores,
    });

    for (const cs of category_scores) {
      if (cs.sub_score == null) continue;
      const acc = hit.category_running.get(cs.slug) ?? { sum: 0, count: 0 };
      acc.sum += cs.sub_score;
      acc.count += 1;
      hit.category_running.set(cs.slug, acc);
    }
  }

  const out: TeamScoreBreakdown[] = Array.from(map.values())
    .sort((a, b) => a.sequence - b.sequence)
    .map((d) => {
      const numeric = d.judge_scores
        .map((s) => s.score)
        .filter((s): s is number => s != null);
      const avg_score =
        numeric.length > 0 ? numeric.reduce((sum, s) => sum + s, 0) / numeric.length : null;
      const category_avgs: Record<string, number> = {};
      for (const [slug, acc] of d.category_running) {
        if (acc.count > 0) category_avgs[slug] = Math.round((acc.sum / acc.count) * 100) / 100;
      }
      return {
        deliverable_name: d.name,
        deliverable_sequence: d.sequence,
        point_value: d.point_value,
        is_locked_gate: d.is_locked_gate,
        judge_scores: d.judge_scores,
        avg_score,
        category_avgs,
      };
    });

  return out;
}

type RawSubmissionRow = {
  id: string;
  deliverable_id: string;
  team_id: string;
  file_url: string;
  file_name: string;
  file_size_bytes: number | null;
  status: SubmissionStatus;
  submitted_at: string;
};

export async function listCompetitionSubmissions(
  competitionId: string
): Promise<CompetitionSubmissionRow[]> {
  const { data, error } = await supabase
    .from("submissions")
    .select("id, deliverable_id, team_id, file_url, file_name, file_size_bytes, status, submitted_at")
    .eq("competition_id", competitionId);
  if (error || !data?.length) return [];

  const deliverableIds = Array.from(new Set((data as RawSubmissionRow[]).map((r) => r.deliverable_id)));
  const teamIds = Array.from(new Set((data as RawSubmissionRow[]).map((r) => r.team_id)));

  const [{ data: dels }, { data: teams }] = await Promise.all([
    supabase.from("deliverables").select("id, name, sequence").in("id", deliverableIds),
    supabase.from("teams").select("id, name").in("id", teamIds),
  ]);

  const dById = new Map((dels ?? []).map((d) => [d.id, d]));
  const tById = new Map((teams ?? []).map((t) => [t.id, t]));

  const out: CompetitionSubmissionRow[] = [];
  for (const r of data as RawSubmissionRow[]) {
    const d = dById.get(r.deliverable_id);
    const t = tById.get(r.team_id);
    if (!d || !t) continue;
    out.push({
      submission_id: r.id,
      deliverable_id: r.deliverable_id,
      deliverable_name: d.name,
      deliverable_sequence: d.sequence,
      team_id: r.team_id,
      team_name: t.name,
      file_url: r.file_url,
      file_name: r.file_name,
      file_size_bytes: r.file_size_bytes,
      status: r.status,
      submitted_at: r.submitted_at,
    });
  }

  return out.sort(
    (a, b) => a.deliverable_sequence - b.deliverable_sequence || a.team_name.localeCompare(b.team_name)
  );
}
